import { io, type Socket } from 'socket.io-client'
import { useChannelStore } from '@/shell/stores/channelStore'
import { useServerStore } from '@/shell/stores/serverStore'
import { useAuthStore } from '../stores/authStore'
import { useDmStore } from '@os/messenger/web/stores/dmStore'
import { useVoiceStore } from '../stores/voiceStore'
import { useToastStore } from '../stores/toastStore'
import { sounds } from './sounds'
import { showMessageNotification } from './notifications'
import { BACKEND_URL, IS_NATIVE_SHELL } from './config'
import { dmApi, refreshAccessToken } from './api'
import { detectClientInfo, detectClientInfoAsync } from '../utils/clientInfo'
import { installAppPresence } from './appPresence'

let socket: Socket | null = null

// OSPROD-270: resolve the CURRENT auth token from localStorage. Shared by
// initial connect, every reconnect attempt, and the connect_error recovery
// path — the token rotates hourly (OSPROD-240 silent JWT refresh), so a
// value captured once at socket creation goes stale within the hour.
function currentAuthToken(): string {
  const token = localStorage.getItem('token')
  const guestToken = localStorage.getItem('guestToken')
  return token || (guestToken?.startsWith('guest:') ? guestToken : guestToken ? `guest:${guestToken}` : '')
}

// Handshake denials from the server auth middleware that mean "your access
// token is stale/expired" — recoverable by minting a fresh access token via
// the refresh token. Deliberately NOT account_disabled / account_deleted /
// guest-session errors: those are fatal and retrying would loop.
const RECOVERABLE_AUTH_ERRORS = new Set(['Invalid token', 'Authentication required'])
let authRecoveryAt = 0

export const getSocket = () => {
  if (!socket) {
    // Reuse existing socket from previous HMR cycle (includes reconnecting state)
    const existing = (window as any).__socket as Socket | undefined
    if (existing && !existing.disconnected) {
      socket = existing
      installAppPresence(socket)
      return socket
    }

    const authToken = currentAuthToken()
    // OSPROD-81: piggyback the detected client info on the handshake
    // auth so every reconnect re-publishes it without an explicit
    // round-trip. Brave's vendor check is async — kick it off and
    // re-emit if it upgrades from "Chrome" to "Brave".
    const clientInfo = detectClientInfo()
    socket = io(IS_NATIVE_SHELL ? BACKEND_URL : '/', {
      auth: { token: authToken, clientInfo },
      transports: ['websocket', 'polling'],
      reconnection: true,
      // OSPROD-270: was 5. On the desktop app the page lives for DAYS —
      // one laptop sleep longer than 5 backoff attempts (~15s) and the
      // socket gave up forever: no unread badges, no toasts, no message
      // sound, while REST kept working so the app looked alive. Browsers
      // masked this because tabs get reloaded constantly. socket.io's
      // default is Infinity; restore it and cap the backoff.
      reconnectionAttempts: Infinity,
      reconnectionDelayMax: 15_000,
    })
    ;(window as any).__socket = socket
    installAppPresence(socket)

    // OSPROD-270 resilience — three layers, all needed:
    //
    // 1. Every reconnect attempt re-reads the token from localStorage.
    //    The access token rotates hourly (OSPROD-240); the value captured
    //    at io() creation is stale after the first rotation, so a network
    //    blip after >1h uptime reconnected with a dead token.
    socket.io.on('reconnect_attempt', () => {
      const s = socket
      if (!s) return
      ;(s as any).auth = { ...((s as any).auth || {}), token: currentAuthToken() }
    })

    // 2. A server-side handshake denial ('Invalid token') is FATAL to
    //    socket.io — it stops retrying (socket.active === false). When the
    //    denial is just an expired access token, mint a fresh one via the
    //    single-flight refresh and manually reconnect. Throttled so a dead
    //    refresh token can't hot-loop; fatal denials (account_disabled,
    //    guest expiry) are excluded and stay dead by design.
    socket.on('connect_error', (err: Error) => {
      const s = socket
      if (!s || s.connected) return
      if (!RECOVERABLE_AUTH_ERRORS.has(err?.message)) return
      const now = Date.now()
      if (now - authRecoveryAt < 10_000) return
      authRecoveryAt = now
      // Guests have no refresh token — just retry with whatever is current.
      if (!localStorage.getItem('refreshToken')) {
        if (currentAuthToken()) {
          ;(s as any).auth = { ...((s as any).auth || {}), token: currentAuthToken() }
          s.connect()
        }
        return
      }
      refreshAccessToken().then((newToken) => {
        const sock = socket
        if (!sock || sock.connected) return
        if (!newToken) return // refresh path exhausted — api.ts handles logout
        ;(sock as any).auth = { ...((sock as any).auth || {}), token: newToken }
        console.warn('[socket] handshake auth failed — reconnecting with refreshed token')
        sock.connect()
      }).catch(() => {})
    })

    // 3. When api.ts silently refreshes the token (any 401-triggered
    //    refresh), swap it into the live socket immediately and revive a
    //    socket that already gave up.
    window.addEventListener('rx:token-refreshed', (e: Event) => {
      const s = socket
      if (!s) return
      const token = (e as CustomEvent).detail?.token
      if (typeof token !== 'string' || !token) return
      ;(s as any).auth = { ...((s as any).auth || {}), token }
      if (!s.connected && !(s as any).active) s.connect()
    })

    // Post-sleep / network-change safety net: the OS 'online' signal fires
    // well after socket.io's own backoff may have been suppressed by the
    // sleeping event loop — kick a disconnected socket explicitly.
    window.addEventListener('online', () => {
      const s = socket
      if (!s || s.connected) return
      ;(s as any).auth = { ...((s as any).auth || {}), token: currentAuthToken() }
      try { s.connect() } catch { /* ignore */ }
    })

    detectClientInfoAsync().then((info) => {
      // Re-emit when ANY field changed (Brave label OR the late-arriving
      // nativeBuild), not just the label.
      if (JSON.stringify(info) === JSON.stringify(clientInfo)) return
      const s = socket
      if (!s) return
      ;(s as any).auth = { ...((s as any).auth || {}), clientInfo: info }
      // Push the upgraded label to the server so an in-flight voice
      // session reflects it immediately, not just on next reconnect.
      try { s.emit('voice:client-info', { clientInfo: info }) } catch { /* ignore */ }
    }).catch(() => {})

    // Global message listeners — never removed, so they survive HMR / reconnect

    // Admin changed a system sound — refetch the resolved config so this
    // client uses the new mapping without a reload.
    socket.on('system-sounds:updated', () => { void sounds.loadConfig() })

    socket.on('message:new', (message: any) => {
      if (message.channelId) {
        // Client-First reconciliation: if the broadcast carries the
        // localId we sent with our optimistic bubble, swap the pending
        // placeholder for the real row in one atomic state update.
        // Otherwise fall back to plain append (idempotent on id).
        const localId: string | undefined = message.localId
        const store = useChannelStore.getState()
        const matched = localId
          ? store.confirmPendingMessage(message.channelId, localId, message)
          : false
        if (!matched) {
          store.addMessage(message.channelId, message)
        }
        const myId = useAuthStore.getState().user?.id
        const isMentioned = message.content?.includes(`<@${myId}>`) || message.content?.includes('@everyone')
        if (isMentioned) {
          sounds.notification()
        } else if (message.authorId !== myId) {
          sounds.message()
        }
        // Track unread for the Business OS rail badge: bump if the message
        // is from someone else AND the user isn't currently focused on
        // this channel. setActiveChannel clears the flag on navigation.
        if (message.authorId !== myId) {
          const activeId = useChannelStore.getState().activeChannelId
          if (activeId !== message.channelId) {
            useChannelStore.getState().markChannelUnread(message.channelId)
          }
        }
        // OS notification — channels were missing this surface entirely
        // before. Mentions are handled by the dedicated `message:mention`
        // event (which works even for users not in the channel room),
        // so here we only fire for the "channel inactive, no mention"
        // case to avoid double-notifying mentioned channel members.
        if (message.authorId !== myId) {
          const activeId = useChannelStore.getState().activeChannelId
          const channelInactive = activeId !== message.channelId
          if (channelInactive && !isMentioned) {
            // Resolve a friendly title — "Author in #channel-name".
            // Falls back gracefully if the channel cache hasn't loaded.
            let channelName: string | null = null
            const servers = useServerStore.getState().servers
            for (const s of servers) {
              const direct = s.channels?.find((c: any) => c.id === message.channelId)
              if (direct) { channelName = direct.name; break }
              for (const cat of s.categories || []) {
                const inCat = cat.channels?.find((c: any) => c.id === message.channelId)
                if (inCat) { channelName = inCat.name; break }
              }
              if (channelName) break
            }
            const author = message.author
            const authorName = author?.firstName
              ? `${author.firstName} ${author.lastName ?? ''}`.trim()
              : author?.username ?? 'Someone'
            const title = channelName ? `${authorName} in #${channelName}` : authorName
            const body = message.content
              || (message.photoUrl ? 'Photo'
                : message.videoUrl ? 'Video'
                : message.audioUrl ? 'Voice message'
                : message.stickerId ? 'Sticker'
                : message.gifUrl ? 'GIF'
                : 'Message')
            showMessageNotification({
              title,
              body,
              channelId: message.channelId,
              messageId: message.id,
              tag: `chat-${message.channelId}`,
            })
          }
        }
        // OSPROD-52 / OSPROD-73: voice-side-panel Chat tab badge. The
        // tab lives in the voice room UI, so we only bump while the
        // user is connected to THIS channel's voice session AND not
        // actually looking at the chat — i.e. either (a) the Chat tab
        // isn't active, or (b) the whole panel is collapsed (the
        // OSPROD-73 case where the user maximised the grid). Opening
        // the Chat tab or expanding the panel clears the badge
        // atomically (see voiceStore.setSidePanelTab /
        // setSidePanelCollapsed).
        if (message.authorId !== myId) {
          const voice = useVoiceStore.getState()
          const looking = !voice.isSidePanelCollapsed && voice.sidePanelTab === 'chat'
          if (voice.currentChannelId === message.channelId && !looking) {
            voice.bumpSidePanelChatUnread()
          }
        }
      }
    })

    socket.on('dm:message:new', ({ dmChannelId, message }: { dmChannelId: string; message: any }) => {
      // Same Client-First reconciliation as channels — match by localId
      // when present, otherwise idempotent append.
      const localId: string | undefined = message.localId
      const store = useChannelStore.getState()
      const matched = localId
        ? store.confirmPendingMessage(`dm-${dmChannelId}`, localId, message)
        : false
      if (!matched) {
        store.addMessage(`dm-${dmChannelId}`, message)
      }
      const myId = useAuthStore.getState().user?.id
      if (message.authorId !== myId) {
        sounds.message()
      }

      // Update DM list + unread badge globally so the rail counter reacts
      // even when the user is in another section (Business OS, dock, etc.)
      // and TelegramChatList isn't mounted to handle this socket itself.
      const fromMe = message.authorId === myId
      const isViewingThis = window.location.pathname === `/channels/dm-${dmChannelId}`
      const shouldBump = !fromMe && !isViewingThis
      useDmStore.getState().setDmChannels((prev) => {
        const existing = prev.find((c) => c.id === dmChannelId)
        if (!existing) {
          // Race fallback: dm:channel:created didn't arrive (or hasn't
          // arrived yet) for this DM — most often when the user is
          // added to a group that's already actively receiving
          // messages, so the message emit beat the channel-created
          // emit on the wire. Lazy-fetch the list once so the row
          // shows up with the right unread count from the server.
          // addDmChannel is idempotent if dm:channel:created later
          // wins the race.
          dmApi.list().then((res) => {
            const fresh = (res.data as any[]).find((d) => d.id === dmChannelId)
            if (fresh) useDmStore.getState().addDmChannel(fresh)
          }).catch(() => {})
          return prev
        }
        const lastKnownAt = new Date(existing.updatedAt).getTime()
        const incomingAt = new Date(message.createdAt).getTime()
        const lastKnownId = existing.messages?.[0]?.id
        if (lastKnownId === message.id) return prev
        if (incomingAt <= lastKnownAt) return prev
        // OSPROD-77: while the user is on the DM URL, force the count
        // to 0 rather than just preserving whatever was there. Without
        // this, a count that leaked through an earlier race (e.g. an
        // in-flight dmApi.list refresh that resolved AFTER the chat
        // opened with a stale server value) would stick around even
        // though the user is actively reading the chat.
        const nextUnread = isViewingThis
          ? 0
          : shouldBump
            ? (existing.unreadCount ?? 0) + 1
            : existing.unreadCount
        const updated = prev.map((dm) =>
          dm.id === dmChannelId
            ? {
                ...dm,
                messages: [message],
                updatedAt: message.createdAt,
                unreadCount: nextUnread,
              }
            : dm
        )
        updated.sort((a, b) => {
          if (a.isPinned && !b.isPinned) return -1
          if (!a.isPinned && b.isPinned) return 1
          return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        })
        return updated
      })
    })

    // Mention fan-out — server emits this per mentioned user, regardless
    // of channel membership / current view. Always fires an OS-level
    // notification (forceShow) and uses a mention-specific tag so the
    // generic chat tag doesn't collapse it. Resolves friendly title
    // from the cached server / DM rosters.
    socket.on('message:mention', (data: {
      messageId: string
      content: string
      channelId: string | null
      dmChannelId: string | null
      source: 'channel' | 'dm'
      author: { id: string; username: string; firstName?: string | null; lastName?: string | null } | null
      createdAt: string
    }) => {
      const myId = useAuthStore.getState().user?.id
      // Server already excludes the author, but defense in depth — no
      // self-mention notifications.
      if (data.author?.id === myId) return
      let title = data.author?.firstName
        ? `${data.author.firstName} ${data.author.lastName ?? ''}`.trim()
        : (data.author?.username ?? 'Someone')
      // Append context — channel name or DM partner — if we have it.
      if (data.source === 'channel' && data.channelId) {
        const servers = useServerStore.getState().servers
        let channelName: string | null = null
        for (const s of servers) {
          const direct = s.channels?.find((c: any) => c.id === data.channelId)
          if (direct) { channelName = direct.name; break }
          for (const cat of s.categories || []) {
            const inCat = cat.channels?.find((c: any) => c.id === data.channelId)
            if (inCat) { channelName = inCat.name; break }
          }
          if (channelName) break
        }
        if (channelName) title = `${title} in #${channelName}`
      }
      const body = data.content || 'mentioned you'
      showMessageNotification({
        title: `@ ${title}`,
        body,
        channelId: data.channelId ?? undefined,
        dmChannelId: data.dmChannelId ?? undefined,
        messageId: data.messageId,
        forceShow: true,
        tag: `mention-${data.messageId}`,
      })
      sounds.notification()
    })

    socket.on('message:updated', (message: any) => {
      if (message.channelId) {
        useChannelStore.getState().updateMessage(message.channelId, message)
      }
      if (message.dmChannelId) {
        useChannelStore.getState().updateMessage(`dm-${message.dmChannelId}`, message)
      }
    })

    socket.on('message:deleted', ({ messageId }: { messageId: string }) => {
      const allMessages = useChannelStore.getState().messages
      for (const [channelId, msgs] of Object.entries(allMessages)) {
        if (msgs.some((m) => m.id === messageId)) {
          useChannelStore.getState().deleteMessage(channelId, messageId)
          break
        }
      }
    })

    // Communication governance — post-delivery hide. Payload carries only
    // { messageId, ruleName[, dmChannelId] } (no channelId — channel
    // messages are hidden via a room-wide emit, not a per-channelId one),
    // so we locate the message the same way message:deleted does: scan
    // every bucket in the store for a matching id. DM buckets live under
    // `dm-<dmChannelId>` in the same `messages` map, so this covers both
    // surfaces without branching on payload.dmChannelId. Only `policyHidden`
    // is patched in — the bubble itself renders the placeholder and hides
    // content/media/reactions once that field is set (see MessageItem /
    // TelegramMessageItem), so there's no need to also scrub fields here.
    socket.on('message:policy-hidden', ({ messageId, ruleName }: { messageId: string; ruleName: string; dmChannelId?: string }) => {
      const allMessages = useChannelStore.getState().messages
      for (const [channelId, msgs] of Object.entries(allMessages)) {
        const msg = msgs.find((m) => m.id === messageId)
        if (msg) {
          useChannelStore.getState().updateMessage(channelId, { ...msg, policyHidden: { ruleName } })
          break
        }
      }
    })

    // Communication governance — blocked-send / blocked-edit rejection.
    // Emitted with `localId` for a blocked `message:send` (matches a
    // Client-First optimistic bubble) or `messageId` for a blocked
    // `message:edit` (no optimistic entry exists for edits — the composer
    // isn't tracking a pending state for those). Prefer marking the
    // specific bubble (same failPendingMessage path retryPending/pendingSend
    // already uses for network failures) so the reason renders inline via
    // the bubble's existing AlertCircle + Retry/Discard footer; fall back
    // to a toast when there's no bubble to mark.
    socket.on('message:error', (payload: { message: string; localId?: string; messageId?: string }) => {
      if (payload.localId) {
        const pendingMap = useChannelStore.getState().pendingMessages
        for (const [channelId, list] of Object.entries(pendingMap)) {
          if (list.some((p) => p.localId === payload.localId)) {
            useChannelStore.getState().failPendingMessage(channelId, payload.localId, payload.message)
            return
          }
        }
      }
      useToastStore.getState().showToast({
        id: `message-error-${payload.localId || payload.messageId || Date.now()}`,
        type: 'error',
        title: 'Message not sent',
        body: payload.message,
      })
    })

    socket.on('user:presence', ({ userId, status, lastSeenAt }: { userId: string; status: string; lastSeenAt?: string }) => {
      useDmStore.getState().applyPresence(userId, status, lastSeenAt)
    })

    // Server emits this to every member who was just added to a DM —
    // either a brand-new 1:1 / group, or an existing group they were
    // invited into. Without this hook the chat list would only refresh
    // on the next manual `dmApi.list()` (typically when the user opens
    // the Chats tab), so during a voice call the new conversation —
    // and any messages that arrive in it — would stay invisible until
    // they switched away. addDmChannel is idempotent: merges if the row
    // already exists so we don't clobber in-flight unread bumps.
    socket.on('dm:channel:created', ({ dmChannel }: { dmChannel: any }) => {
      if (dmChannel?.id) useDmStore.getState().addDmChannel(dmChannel)
    })

    // Group/channel was deleted by its creator/admin. Drop it from the
    // list across every member's session in real time so nobody opens a
    // dangling chat. removeDmChannel also clears activeDmId, so anyone
    // currently viewing the deleted chat lands back in the empty state.
    socket.on('dm:channel:deleted', ({ dmChannelId }: { dmChannelId: string }) => {
      useDmStore.getState().removeDmChannel(dmChannelId)
    })

    // Role tag next to messages (OSPROD-43): an admin/creator was
    // promoted, demoted, or ownership changed. Patch only the affected
    // member so the UI re-renders the tag everywhere that user appears
    // — message bubbles, info panel, member list — without a refetch.
    socket.on('dm:member:role-changed', (
      { dmChannelId, userId, role }: { dmChannelId: string; userId: string; role: 'CREATOR' | 'ADMIN' | 'MEMBER' }
    ) => {
      useDmStore.getState().setMemberRole(dmChannelId, userId, role)
    })

    // OSPROD-41: avatar / name / description edits broadcast to every
    // member so chat list rows + open info panels re-render without a
    // page refresh. updateDmChannel merges the payload into the existing
    // entry, preserving messages cache.
    socket.on('dm:channel:updated', ({ dmChannel }: { dmChannel: any }) => {
      if (dmChannel?.id) useDmStore.getState().updateDmChannel(dmChannel)
    })

    socket.on('reaction:updated', ({ messageId, reactions }: { messageId: string; reactions: any[] }) => {
      const myId = useAuthStore.getState().user?.id
      const allMessages = useChannelStore.getState().messages
      for (const [chId, msgs] of Object.entries(allMessages)) {
        const msg = msgs.find((m) => m.id === messageId)
        if (msg) {
          // OSPROD-79: detect a fresh reaction landing on MY message
          // from somebody else. Diff old vs new reaction rows by
          // (userId, emoji) — anything in NEW that wasn't in OLD is a
          // newly added reaction. Drives the chirp + the unread badge.
          const prevSig = new Set((msg.reactions || []).map((r: any) => `${r.userId}:${r.emoji}`))
          const added = reactions.filter((r) => !prevSig.has(`${r.userId}:${r.emoji}`))
          const isMyMessage = msg.authorId === myId
          const someoneElseReacted = added.some((r) => r.userId !== myId)
          if (isMyMessage && someoneElseReacted) {
            sounds.reaction()
            const activeId = useChannelStore.getState().activeChannelId
            if (activeId !== chId) {
              useChannelStore.getState().markChannelUnread(chId)
            }
          }
          msg.reactions = reactions
          useChannelStore.getState().updateMessage(chId, { ...msg })
          break
        }
      }
    })

    // Admin disabled this account (or otherwise revoked the session):
    // tear auth down and hard-redirect to /login. The server also drops
    // the socket + 401s every later API call, so this listener is the
    // instant-UX path; the ?disabled=1 flag lets the login page explain
    // why the user landed back at sign-in.
    socket.on('auth:force-logout', async (payload: { reason?: string } = {}) => {
      try {
        await useAuthStore.getState().logout()
      } catch { /* ignore */ }
      const q = payload.reason === 'account_disabled' ? '?disabled=1' : ''
      window.location.assign(`/login${q}`)
    })

    // Client-First: when the socket connects (initial OR reconnect),
    // replay every pending bubble marked `error` — typically those
    // rehydrated from localStorage on reload, or left behind after a
    // network drop. The retry uses the same `localId` as the original
    // send, and the server is idempotent on it, so retrying after the
    // first send actually landed just returns the existing row +
    // confirms the bubble. `sending` bubbles are left alone — they
    // might still be in-flight; the next reconnect cycle will mop up
    // anything that genuinely got stuck.
    socket.on('connect', () => {
      // Async-import to avoid the import cycle with channelStore /
      // pendingSend that re-imports getSocket at module top.
      import('./pendingSend').then(({ retryAllErroredPending }) => {
        retryAllErroredPending()
      }).catch(() => {})
      // Re-push clientInfo on every (re)connect so the server's
      // in-memory `voiceClientInfoByUser` cache repopulates after
      // restarts / fail-overs. Without this, users who joined voice
      // before the server bounced have no client badge visible to
      // their peers ("4-6 of 20 show a client" symptom). Cheap one-
      // message round trip; the server handler is a no-op when the
      // user isn't actually in voice.
      try {
        const s = socket
        if (s) s.emit('voice:client-info', { clientInfo: (s as any).auth?.clientInfo })
      } catch { /* ignore */ }
    })
  }
  return socket
}

export const disconnectSocket = () => {
  if (socket) {
    socket.disconnect()
    socket = null
  }
}

export const reconnectSocket = () => {
  const authToken = currentAuthToken()

  // Reuse existing socket if connected with same token
  const existing = (window as any).__socket as Socket | undefined
  const existingToken = (existing as any)?.auth?.token
  if (existing?.connected && existingToken === authToken) {
    return existing
  }

  disconnectSocket()
  return getSocket()
}
